import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { AuthLoading } from './AuthLoading';
import type { Role } from '../constants/roles';
import type { RolePermissions } from '../constants/roles';
import { BrowserCompatWrapper } from '@/components/ui/BrowserCompatWrapper';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: Role | Role[];
  requiredPermissions?: (keyof RolePermissions)[];
  redirectTo?: string;
}

/**
 * Component to protect routes based on authentication and roles
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole,
  requiredPermissions,
  redirectTo = '/auth'
}) => {
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const { hasRole, hasPermissions } = useRoleAccess();

  // Show loading while authentication initializes
  if (isLoading) {
    return <AuthLoading />;
  }

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check required roles
  if (requiredRole && !hasRole(requiredRole)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check required permissions
  if (requiredPermissions && !hasPermissions(requiredPermissions)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <BrowserCompatWrapper>{children}</BrowserCompatWrapper>;
}; 